/**
 * Shared SSE (Server-Sent Events) streaming utilities.
 *
 * Used by ChatService.js (chat messages) and useBaseChatHistory.js (greetings)
 * to avoid duplicating fetch + auth + parsing logic.
 */

import { clerk } from '../services/clerkConfig';
import { Mixpanel } from '../services/Mixpanel';
import { getAPIBaseURL } from './environment';

/**
 * Build HTTP headers for an authenticated SSE / streaming fetch request.
 * Mirrors the headers that axiosConfig.js adds to every Axios request.
 *
 * @returns {Promise<Object>} Headers object ready to pass to fetch()
 */
export async function getStreamAuthHeaders() {
  const headers = {
    'Content-Type': 'application/json',
    'X-Country-Code': localStorage.getItem('country_code') || '',
    'LD-Session': localStorage.getItem('diffitSessionId') || '',
    'X-Client-Commit-SHA':
      typeof __VITE_VERCEL_COMMIT_SHA__ !== 'undefined' ? __VITE_VERCEL_COMMIT_SHA__ : 'dev',
    // `|| ''` because both resolve undefined whenever Mixpanel didn't
    // initialize — a bot session, or a real visitor behind an ad blocker.
    // Unlike Axios (which strips nullish headers), the Headers constructor
    // stringifies undefined, so the backend would receive the literal
    // "undefined" as a distinct id / bucketing key.
    'X-Mixpanel-Distinct-ID': (await Mixpanel.getDistinctId()) || '',
    // Bucketing unit for LaunchDarkly experiment flags — see axiosConfig.js.
    'X-Mixpanel-Device-ID': (await Mixpanel.getDeviceId()) || '',
  };

  await clerk.load();
  if (clerk.session) {
    const token = await clerk.session.getToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  }

  return headers;
}

/**
 * Async generator that reads an SSE response body and yields parsed JSON event objects.
 *
 * Only lines starting with "data: " are parsed; others (comments, empty lines) are skipped.
 *
 * @param {Response} response - A fetch() Response with a readable body stream
 * @yields {Object} Parsed SSE event payloads, e.g. { type: "text_delta", content: "..." }
 */
export async function* parseSSEStream(response) {
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  // An event is held until its blank-line boundary: FastAPI writes its id: line after data:.
  let pendingEvent = null;
  let pendingEventId = null;

  function* processLine(line) {
    if (line.startsWith('id: ')) {
      pendingEventId = line.slice(4).trim();
      if (pendingEvent) pendingEvent._eventId = pendingEventId;
    } else if (line.startsWith('data: ')) {
      if (pendingEvent) yield pendingEvent;
      pendingEvent = null;
      try {
        pendingEvent = JSON.parse(line.slice(6));
        if (pendingEventId) {
          pendingEvent._eventId = pendingEventId;
        }
      } catch {
        // Skip malformed events silently
      }
    } else if (line.startsWith(':')) {
      // SSE comment (e.g. keepalive ping) — yield so callers can reset inactivity timers
      yield { type: 'keepalive' };
    } else if (line === '') {
      // SSE event boundary — emit the event and reset its id
      if (pendingEvent) yield pendingEvent;
      pendingEvent = null;
      pendingEventId = null;
    }
  }

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    // Keep the last (possibly incomplete) line in the buffer
    buffer = lines.pop();

    for (const line of lines) {
      yield* processLine(line);
    }
  }

  // Flush any remaining complete events in the buffer
  for (const line of buffer.split('\n')) {
    yield* processLine(line);
  }
  if (pendingEvent) yield pendingEvent;
}

/**
 * Return the resolved API base URL (same as getAPIBaseURL from environment.js).
 * Re-exported here so consumers only need one import.
 */
export { getAPIBaseURL };
